<template>
    <v-card>
        <v-card-title>
            <v-layout class="title">
                <v-btn :disabled="getQuestionIdx == 0" @click="previous" class="mr-7">
                    <v-icon left>keyboard_arrow_left</v-icon>
                    {{ $t('previous') }}
                </v-btn>

                <div v-if="$vuetify.breakpoint.lgAndUp">
                    {{ $i18n.t('question') + ' #' + getQuestion.id }}
                </div>
                <div v-else>
                    {{ '#' + getQuestion.id }}
                </div>

                <v-spacer/>

                <v-tooltip v-if="$route.name=='Questions'" open-on-hover bottom>
                    <template v-slot:activator="{ on }">
                        <div v-on="!getQuestion.removable ? on : ''">
                            <v-btn :disabled="!getQuestion.removable" @click="removing = true">
                                {{ $t('removeQuestion') }}
                                <v-icon right>clear</v-icon>
                            </v-btn>
                        </div>
                    </template>
                    <span>{{ $i18n.t('questionNotRemovable') }}</span>
                </v-tooltip>

                <v-btn v-if="$route.name=='Questions'" @click="edit" color="teal" class="ml-7">
                    {{ $t('editQuestion') }}
                    <v-icon right>edit</v-icon>
                </v-btn>

                <template v-if="$route.name=='AddQuestions'">
                    <v-btn v-if="getTestQuestions.includes(getQuestion.id)" @click="removeFromTest" class="ml-7">
                        {{ $t('removeFromTest') }}
                        <v-icon right>remove</v-icon>
                    </v-btn>

                    <v-btn v-else @click="addToTest" color="teal" class="ml-7">
                        {{ $t('addToTest') }}
                        <v-icon right>add</v-icon>
                    </v-btn>
                </template>

                <v-btn :disabled="getQuestionIdx == getQuestions.length-1" @click="next" class="ml-7">
                    {{ $t('next') }}
                    <v-icon right>keyboard_arrow_right</v-icon>
                </v-btn>
            </v-layout>
        </v-card-title>

        <v-card-text>
            <v-container grid-list-xl>
                <v-expand-transition>
                    <v-card v-show="removing" height="57" class="mx-auto">
                        <v-alert v-show="removing" outlined type="error" class="mb-n1">
                            <v-layout row>
                                <div  class="my-auto ml-4">
                                    {{ $i18n.t('confirmRemoveQuestion') }}
                                </div>
                                <v-spacer/>
                                <v-btn @click="remove" color="red darken-1" class="mr-4">
                                    {{ $t('remove') }}
                                    <v-icon right>delete</v-icon>
                                </v-btn>
                                <v-btn @click="removing = false" class="mr-4">
                                    {{ $t('cancel') }}
                                </v-btn>
                            </v-layout>
                        </v-alert>
                    </v-card>
                </v-expand-transition>

                <v-alert v-show="getQuestion.hidden" outlined type="info" class="mb-n1 mt-3">
                    <v-layout row>
                        <div  class="my-auto ml-4">
                            {{ $i18n.t('provisionalQuestion') }}
                        </div>
                        <v-btn @click="approve" color="blue" class="ml-auto mr-4">
                            {{ $t('approve') }}
                            <v-icon right>done</v-icon>
                        </v-btn>
                    </v-layout>
                </v-alert>

                <v-layout col class="headline">
                    {{ getQuestion.text }}
                </v-layout>

                <v-layout row align-center>
                    <v-flex :xs7="getQuestion.image">
                        <v-row v-for="answer in getQuestion.answers" :key="answer.id">
                            <v-col cols=1>
                                <div class="body-1 font-weight-medium ml-7">
                                    {{ ['A','B','C','D','E','F','G','H','I','J'][answer.position-1] }}
                                </div>
                            </v-col>
                            <v-col cols=11 v-if="answer.position == getQuestion.correctAnswer" class="body-1 font-weight-bold black--text">
                                {{ answer.text }}
                            </v-col>
                            <v-col cols=11 v-else class="body-1 font-weight-light black--text">
                                {{ answer.text }}
                            </v-col>
                        </v-row>
                    </v-flex>

                    <v-flex xs5 v-if="isLoading && !getQuestion.imageString && !!getQuestion.image" class="text-center">
                        <v-progress-circular :width="3" :size="70" indeterminate color="teal"/>
                    </v-flex>

                    <v-flex xs5 v-if="getQuestion.imageString && !!getQuestion.image" class="text-center">
                        <v-img :src="getQuestion.imageString"  contain height="250px"/>

                        <v-btn v-if="$route.name=='Questions'" @click="downloadImage" class="mt-3">
                            {{ $t('downloadImage') }}
                            <v-icon right>get_app</v-icon>
                        </v-btn>
                    </v-flex>
                </v-layout>


                <v-divider v-if="!!getQuestion.notes || !!getQuestion.title"/>

                <v-list v-if="!!getQuestion.notes || !!getQuestion.title">
                    <v-list-item v-if="!!getQuestion.title">
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('title') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ getQuestion.title }}</v-flex>
                    </v-list-item>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('observations') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ getQuestion.notes }}</v-flex>
                    </v-list-item>
                </v-list>

                <v-divider v-if="!!getQuestion.difficulty && !!getQuestion.discrimination"/>

                <v-list v-if="!!getQuestion.difficulty && !!getQuestion.discrimination">
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">
                            <v-tooltip open-on-hover bottom>
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        {{ $i18n.t('discrimination') }}
                                    </div>
                                </template>
                                <span>{{ $i18n.t('discriminationTooltip') }}</span>
                            </v-tooltip>
                        </v-flex>

                        <v-flex class="subheading font-weight-light">{{ (getQuestion.discrimination).toFixed(2) }}</v-flex>
                    </v-list-item>

                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">
                            <v-tooltip open-on-hover bottom>
                                <template v-slot:activator="{ on }">
                                    <div v-on="on">
                                        {{ $i18n.t('difficulty') }}
                                    </div>
                                </template>
                                <span>{{ $i18n.t('difficultyTooltip') }}</span>
                            </v-tooltip>
                        </v-flex>

                        <v-flex class="subheading font-weight-light">{{ (getQuestion.difficulty).toFixed(2) }}</v-flex>
                    </v-list-item>
                </v-list>

                <v-divider/>

                <v-list>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('topic') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ getQuestion.topicNumber + '. ' + getTopics.find(topic => topic.id==getQuestion.topicId).name }}</v-flex>
                    </v-list-item>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('advanced') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ getQuestion.basic ? $i18n.t('no') : $i18n.t('yes') }}</v-flex>
                    </v-list-item>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('modificationDate') }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-if="getQuestion.lastUpdatedBy == 1">{{ toDate(getQuestion.lastUpdatedAt) }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-else>{{ toDate(getQuestion.lastUpdatedAt) + " " + $i18n.t('by') + " " + getQuestion.lastUpdatedByName }}</v-flex>
                    </v-list-item>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('creationDate') }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-if="getQuestion.createdBy == 1">{{ toDate(getQuestion.createdAt) }}</v-flex>
                        <v-flex class="subheading font-weight-light" v-else>{{ toDate(getQuestion.createdAt) + " " + $i18n.t('by') + " " + getQuestion.createdByName }}</v-flex>
                    </v-list-item>
                </v-list>

                <v-divider/>

                <v-list>
                    <v-list-item>
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('testsAppearances') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ formulations.length }}</v-flex>
                    </v-list-item>
                    <v-list-item v-if="formulations.length > 0">
                        <v-flex xs3 class="subheading font-weight-medium">{{ $i18n.t('lastAppearance') }}</v-flex>
                        <v-flex class="subheading font-weight-light">{{ toDate(formulations.map(formulation => formulation.test.testDate).sort().reverse()[0]) }}</v-flex>
                    </v-list-item>
                </v-list>


                <v-simple-table class="ml-7" v-if="formulations.length > 0">
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">{{ $i18n.t('title') }}</th>
                                <th class="text-left">{{ $i18n.t('testDate') }}</th>
                                <th class="text-center">{{ $i18n.t('questionDiscrimination') }}</th>
                                <th class="text-center">{{ $i18n.t('questionDifficulty') }}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="formulation in formulations" :key="formulation.id">
                                <td>{{ formulation.test.title }}</td>
                                <td>{{ toDate(formulation.test.testDate) }}</td>
                                <td class="text-center">{{ formulation.discrimination || "-" }}</td>
                                <td class="text-center">{{ formulation.difficulty || "-" }}</td>
                            </tr>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-container>
        </v-card-text>

        <v-card-actions>
            <v-spacer/>
            <v-btn @click="$store.commit('setViewQuestionDialog', false)" class="mr-3 mb-3">
                {{ $t('exit') }}
                <v-icon right>clear</v-icon>
            </v-btn>
        </v-card-actions>
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex';
import { i18n } from '@/main.js' 
import toDate from '@/components/dateParser';

import Blob from 'blob';
import FileSaver from 'file-saver';

export default {
    methods:{
        next(){
            this.$store.commit('nextQuestion')
            this.$store.commit('setRemovingQuestion', false)
            this.$store.dispatch('getFormulationsByQuestion', this.getQuestion.id)
            if (this.getQuestion.image)
                this.$store.dispatch('getImage', this.getQuestion.id)
        },
        previous(){
            this.$store.commit('previousQuestion')
            this.$store.commit('setRemovingQuestion', false)
            this.$store.dispatch('getFormulationsByQuestion', this.getQuestion.id)
            if (this.getQuestion.image)
                this.$store.dispatch('getImage', this.getQuestion.id)
        },
        edit(){
            this.$store.commit('setEditingQuestion', true)
            this.$store.commit('resetQuestionValues')
        },
        remove(){
            this.$store.dispatch('removeQuestion')
        },
        downloadImage(){
            var byteCharacters = atob(this.getQuestion.imageString.substring(20));
            var byteNumbers = new Array(byteCharacters.length);
            for (var i = 0; i < byteCharacters.length; i++)
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            var byteArray = new Uint8Array(byteNumbers);

            var extension = ""
            if (this.getQuestion.imageString.indexOf("png") !== -1) extension = "png"
            else if (this.getQuestion.imageString.indexOf("jpg") !== -1 || this.getQuestion.imageString.indexOf("jpeg") !== -1) extension = "jpg"
            else extension = "tiff"

            var blob = new Blob([byteArray], { type: "image/*" });

            FileSaver.saveAs(blob, this.getQuestion.id + "." + extension);
        },
        approve(){
            this.getQuestion.hidden = false
            this.$store.commit('resetQuestionValues')
            this.$store.dispatch('updateQuestion')
        },
        addToTest(){
            this.$store.dispatch('addFormulation', { questionId: this.getQuestion.id, testId: this.$route.params.id })
        },
        removeFromTest(){
            this.$store.dispatch('deleteFormulation', this.getQuestion.id)
        },
    },

    computed:{
        ...mapGetters([
            'isLoading',
            'getQuestions',
            'getQuestion',
            'getQuestionIdx',
            'getTopics',
            'getFormulationsByQuestion',
            'isRemovingQuestion',
            'getTestQuestions',
        ]),
        formulations(){
            return this.getFormulationsByQuestion.filter(formulation => !formulation.test.draft) // Remove draft exams
        },
        removing: {
            get() { return this.isRemovingQuestion },
            set(value) { this.$store.commit('setRemovingQuestion', value) }
        }
    },

    mixins: [toDate],
}
</script>
